import Axios from "axios";
import { PayPal } from "./constants";

const getPayPalCredentials = () => {
  const isSandbox = PayPal.mode === "sandbox";
  return {
    clientId: isSandbox ? PayPal.testClientID : PayPal.clientID,
    clientSecret: isSandbox ? PayPal.testSecretKey : PayPal.SecrectKey,
    subUrl: isSandbox ? PayPal.testSubUrl : PayPal.PayPaSubUrl,
    tokenUrl: isSandbox ? PayPal.testTokenUrl : PayPal.payPalTokenUrl,
  };
};

const getPayPalAccessToken = async () => {
  const { clientId, clientSecret, tokenUrl } = getPayPalCredentials();
  const response = await fetch(tokenUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      Authorization: "Basic " + btoa(`${clientId}:${clientSecret}`),
    },
    body: "grant_type=client_credentials",
  });
  const data = await response.json();
  return data.access_token;
};

const fetchPayPalSubscription = async (accessToken, subscriptionID) => {
  if (!accessToken || !subscriptionID) return null;
  const { subUrl } = getPayPalCredentials();
  try {
    const response = await Axios.get(`${subUrl}/${subscriptionID}`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    });
    return response.data;
  } catch (err) {
    if (err?.response?.status === 404) {
      console.warn("PayPal subscription not found. Verify mode (sandbox vs live) and subscription ID.");
      return null;
    }
    throw err;
  }
};

const isValidDateValue = (iso) => {
  if (!iso) return false;
  const dt = new Date(iso);
  return !isNaN(dt.getTime());
};

const formatDateOnly = (iso) => {
  if (!isValidDateValue(iso)) return "unknown";
  return new Date(iso).toLocaleDateString("en-US");
};

const parseBillingInfo = (subscriptionData) => {
  if (!subscriptionData || !subscriptionData.billing_info) {
    return {
      nextBilling: "unknown",
      lastPayment: "unknown",
      status: "unknown",
      nextBillingRaw: null,
      lastPaymentRaw: null,
      derivedNextBilling: false,
    };
  }
  const nextBillingCandidate = subscriptionData.billing_info.next_billing_time || null;
  const paymentTime = subscriptionData.billing_info.last_payment?.time || null;

  const nextBillingDate = isValidDateValue(nextBillingCandidate) ? new Date(nextBillingCandidate) : null;
  const paymentDate = isValidDateValue(paymentTime) ? new Date(paymentTime) : null;
  const nowMs = Date.now();

  const nextBillingMs = nextBillingDate ? nextBillingDate.getTime() : NaN;
  const paymentMs = paymentDate ? paymentDate.getTime() : NaN;

  // Treat PayPal's value as valid only if it is a real future date and not the same/past compared to the last payment.
  const hasValidNextBilling =
    Number.isFinite(nextBillingMs) &&
    nextBillingMs > nowMs &&
    (!Number.isFinite(paymentMs) || nextBillingMs > paymentMs);

  const nextBillingRaw = hasValidNextBilling ? nextBillingCandidate : null;
  const lastPaymentRaw = Number.isFinite(paymentMs) ? paymentTime : null;
  const derivedNextBilling = !hasValidNextBilling;

  const lastPayment = lastPaymentRaw ? formatDateOnly(lastPaymentRaw) : "unknown";
  const nextBilling = nextBillingRaw ? formatDateOnly(nextBillingRaw) : "unknown";
  const status = subscriptionData.status || "unknown";
  return {
    nextBilling,
    lastPayment,
    status,
    nextBillingRaw: nextBillingRaw,
    lastPaymentRaw,
    derivedNextBilling,
  };
};

const wait = (ms) => new Promise((res) => setTimeout(res, ms));

const fetchPayPalSubscriptionWithRetry = async (accessToken, subscriptionID, retries = 3, delayMs = 1500) => {
  let lastError = null;
  for (let i = 0; i < retries; i++) {
    try {
      const data = await fetchPayPalSubscription(accessToken, subscriptionID);
      if (!data) return null;
      console.log("billing data", data);
      if (data?.billing_info?.next_billing_time) return data;
    } catch (err) {
      lastError = err;
    }
    if (i < retries - 1) {
      await wait(delayMs);
    }
  }
  if (lastError) throw lastError;
  return null;
};

const resolveBillingInfo = async (subscriptionID, facilitatorToken = null) => {
  // Try facilitator token first (may lack billing info immediately), then fallback to client credentials
  if (facilitatorToken) {
    try {
      const sub = await fetchPayPalSubscriptionWithRetry(facilitatorToken, subscriptionID, 3, 1500);
      const info = parseBillingInfo(sub);
      if (info.nextBilling !== "unknown") return info;
    } catch (e) {
      // swallow and try client credentials
    }
  }
  const clientToken = await getPayPalAccessToken();
  const subWithClient = await fetchPayPalSubscriptionWithRetry(clientToken, subscriptionID, 3, 2000);
  return parseBillingInfo(subWithClient);
};

export {
  getPayPalAccessToken,
  fetchPayPalSubscription,
  parseBillingInfo,
  fetchPayPalSubscriptionWithRetry,
  resolveBillingInfo,
  formatDateOnly,
};
